import React, { useState, useEffect, useRef } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Row,
  Col,
  Tag,
  Space,
  message,
  Popconfirm,
  Card,
  Typography,
  Statistic,
  Upload,
  Tooltip,
  Avatar
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  FileOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  HistoryOutlined,
  UploadOutlined,
  UserOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import moment from 'moment';
import {
  getEmployeeLeaves,
  createEmployeeLeave,
  updateEmployeeLeave,
  deleteEmployeeLeave,
  getEmployees,
  imagepath
} from '../api';
import { useParams, useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

const LeaveHistory = () => {
  const { employeeId } = useParams();
  const navigate = useNavigate();
  const [leaves, setLeaves] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingLeave, setEditingLeave] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(employeeId || null);
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState([]);
  const pickerOpenCountRef = useRef(0);
  const pickerScrollHandlerRef = useRef(null);

  useEffect(() => {
    fetchEmployees();
    fetchLeaves();
  }, [selectedEmployee]);

  useEffect(() => {
    return () => {
      if (pickerScrollHandlerRef.current) {
        document.removeEventListener('wheel', pickerScrollHandlerRef.current, true);
        document.removeEventListener('touchmove', pickerScrollHandlerRef.current, true);
        pickerScrollHandlerRef.current = null;
        pickerOpenCountRef.current = 0;
      }
    };
  }, []);

  // Blocks trackpad/wheel scroll from changing dates while the picker panel is visible
  const blockPickerScroll = (open) => {
    if (!pickerScrollHandlerRef.current) {
      pickerScrollHandlerRef.current = (e) => {
        const t = e.target;
        if (t && (t.closest('.ant-picker-dropdown') || t.closest('.ant-picker'))) {
          e.preventDefault();
          e.stopPropagation();
        }
      };
    }

    if (open) {
      pickerOpenCountRef.current += 1;
      if (pickerOpenCountRef.current === 1) {
        document.addEventListener('wheel', pickerScrollHandlerRef.current, { passive: false, capture: true });
        document.addEventListener('touchmove', pickerScrollHandlerRef.current, { passive: false, capture: true });
      }
      return;
    }

    pickerOpenCountRef.current = Math.max(0, pickerOpenCountRef.current - 1);
    if (pickerOpenCountRef.current === 0 && pickerScrollHandlerRef.current) {
      document.removeEventListener('wheel', pickerScrollHandlerRef.current, true);
      document.removeEventListener('touchmove', pickerScrollHandlerRef.current, true);
    }
  };

  const fetchEmployees = async () => {
    const res = await getEmployees();
    if (res.success) setEmployees(res.data);
  };

  const fetchLeaves = async () => {
    setLoading(true);
    const params = selectedEmployee ? { employee_id: selectedEmployee } : {};
    const res = await getEmployeeLeaves(params);
    setLoading(false);
    if (res.success) {
      setLeaves(res.data);
    } else {
      message.error(res.message || 'Failed to fetch leaves');
    }
  };

  const handleOpenModal = (record = null) => {
    setEditingLeave(record);
    setFileList([]);
    if (record) {
      form.setFieldsValue({
        employee_id: record.employee_id,
        leave_type: record.leave_type,
        dates: [dayjs(record.start_date), dayjs(record.end_date)],
        actual_return_date: record.actual_return_date ? dayjs(record.actual_return_date) : null,
        reason: record.reason,
        status: record.status,
      });
      if (record.leave_application_form) {
        setFileList([
          {
            uid: '-1',
            name: record.original_filename || 'Attachment',
            status: 'done',
            url: `${imagepath}${record.leave_application_form}`,
          },
        ]);
      }
    } else {
      form.resetFields();
      if (selectedEmployee) {
        form.setFieldsValue({ employee_id: parseInt(selectedEmployee) });
      }
    }
    setModalVisible(true);
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    const formData = new FormData();
    formData.append('employee_id', values.employee_id);
    formData.append('leave_type', values.leave_type);
    if (values.dates && values.dates.length === 2 && values.dates[0] && values.dates[1]) {
      formData.append('start_date', values.dates[0].format('YYYY-MM-DD'));
      formData.append('end_date', values.dates[1].format('YYYY-MM-DD'));
    }

    // Explicitly send empty string if cleared to tell backend to set to null
    formData.append('actual_return_date', values.actual_return_date ? values.actual_return_date.format('YYYY-MM-DD') : '');

    formData.append('reason', values.reason || '');
    formData.append('status', values.status || 'approved');

    if (fileList.length > 0 && fileList[0].originFileObj) {
      formData.append('form_file', fileList[0].originFileObj);
    }

    let res;
    if (editingLeave) {
      res = await updateEmployeeLeave(editingLeave.id, formData);
    } else {
      res = await createEmployeeLeave(formData);
    }

    setLoading(false);
    if (res.success) {
      message.success(editingLeave ? 'Leave record updated' : 'Leave record added');
      setModalVisible(false);
      fetchLeaves();
    } else {
      message.error(res.message || 'Action failed');
    }
  };

  const handleDelete = async (id) => {
    const res = await deleteEmployeeLeave(id);
    if (res.success) {
      message.success('Record deleted');
      fetchLeaves();
    } else {
      message.error(res.message || 'Failed to delete');
    }
  };

  const getLeaveStatusTag = (record) => {
    if (record.actual_return_date) {
      const end = moment(record.end_date);
      const actual = moment(record.actual_return_date);
      const diff = actual.diff(end, 'days');
      if (diff > 0) return <Tag color="error">{diff} Days Overdue</Tag>;
      return <Tag color="success">Returned</Tag>;
    }
    const today = moment();
    const end = moment(record.end_date);
    if (today.isAfter(end)) return <Tag color="warning">Pending Return</Tag>;
    return <Tag color="processing">On Leave</Tag>;
  };

  const columns = [
    {
      title: 'Employee Name (ID)',
      dataIndex: ['employee', 'name'],
      key: 'employee',
      hidden: !!selectedEmployee,
      render: (text, record) => (
        <Space onClick={() => navigate(`/employees/${record.employee_id}/view`)} style={{ cursor: 'pointer' }}>
          <Avatar size="small" src={record.employee?.profile_image ? `${imagepath}${record.employee.profile_image}` : null} icon={<UserOutlined />} />
          <Text strong>{text} ({record.employee?.emp_code})</Text>
        </Space>
      )
    },
    {
      title: 'Leave Type',
      dataIndex: 'leave_type',
      key: 'leave_type',
      render: (type) => <Tag color="#108ee9" style={{ borderRadius: 6 }}>{type}</Tag>
    },
    {
      title: 'Start Date',
      dataIndex: 'start_date',
      key: 'start_date',
      render: date => <Text style={{ fontSize: 13 }}>{date ? moment(date).format('DD/MM/YYYY') : '-'}</Text>
    },
    {
      title: 'End Date',
      dataIndex: 'end_date',
      key: 'end_date',
      render: date => <Text style={{ fontSize: 13 }}>{date ? moment(date).format('DD/MM/YYYY') : '-'}</Text>
    },
    {
      title: 'Relieved return',
      dataIndex: 'actual_return_date',
      key: 'actual_return_date',
      render: date => date ? <Text style={{ fontSize: 13 }}>{moment(date).format('DD/MM/YYYY')}</Text> : '-'
    },
    {
      title: 'Relief Status',
      key: 'status_label',
      render: (_, record) => getLeaveStatusTag(record)
    },
    {
      title: 'File',
      dataIndex: 'leave_application_form',
      key: 'docs',
      render: (file) => file ? (
        <a href={`${imagepath}${file}`} target="_blank" rel="noreferrer">
          <Button type="text" icon={<FileOutlined style={{ color: '#1890ff', fontSize: 18 }} />} />
        </a>
      ) : ''
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button size="small" shape="circle" icon={<EditOutlined />} onClick={() => handleOpenModal(record)} />
          <Popconfirm title="Delete this record?" onConfirm={() => handleDelete(record.id)}>
            <Button size="small" shape="circle" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ].filter(col => !col.hidden);

  const stats = {
    total: leaves.length,
    onLeave: leaves.filter(l => !l.actual_return_date && moment().isSameOrAfter(moment(l.start_date), 'day')).length,
    overdue: leaves.filter(l => !l.actual_return_date && moment().isAfter(moment(l.end_date), 'day')).length,
    upcoming: leaves.filter(l => !l.actual_return_date && moment().isBefore(moment(l.start_date), 'day')).length,
  };

  return (
    <div style={{ maxWidth: 1300, margin: '0 auto', padding: '10px' }}>
      <header style={{ marginBottom: 32, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 16 }}>
        <div>
          <Title level={2} style={{ marginBottom: 4 }}>Manage Leave Records</Title>
          <Text type="secondary">Centralized leave and history management for all personnel.</Text>
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <Select
            showSearch
            placeholder="Search / Filter by Employee"
            style={{ width: 300 }}
            allowClear
            value={selectedEmployee ? parseInt(selectedEmployee) : null}
            onChange={(val) => {
              setSelectedEmployee(val);
              if (val) navigate(`/employees/${val}/leaves`);
              else navigate('/leave-history');
            }}
            optionFilterProp="children"
            size="large"
          >
            {employees.map(emp => (
              <Option key={emp.id} value={emp.id}>{emp.name}</Option>
            ))}
          </Select>
          <Button type="primary" icon={<PlusOutlined />} size="large" onClick={() => handleOpenModal()} style={{ height: 40, borderRadius: 8 }}>
            Add Entry
          </Button>
        </div>
      </header>

      <Row gutter={[24, 24]} style={{ marginBottom: 32 }}>
        <Col xs={24} sm={6}>
          <Card bordered={false} style={{ borderRadius: 16, background: '#fff' }}>
            <Statistic title="Total History Volume" value={stats.total} prefix={<HistoryOutlined style={{ color: '#bae7ff' }} />} />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card bordered={false} style={{ borderRadius: 16 }}>
            <Statistic title="Active On Leave" value={stats.onLeave} valueStyle={{ color: '#52c41a' }} prefix={<CalendarOutlined />} />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card bordered={false} style={{ borderRadius: 16 }}>
            <Statistic title="Overdue Returns" value={stats.overdue} valueStyle={{ color: '#f5222d' }} prefix={<CheckCircleOutlined />} />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card bordered={false} style={{ borderRadius: 16 }}>
            <Statistic title="Upcoming Leaves" value={stats.upcoming} valueStyle={{ color: '#1890ff' }} prefix={<PlusOutlined />} />
          </Card>
        </Col>
      </Row>

      <Card style={{ borderRadius: 16, overflow: 'hidden', border: 'none' }} bodyStyle={{ padding: 0 }}>
        <Table columns={columns} dataSource={leaves} loading={loading} rowKey="id" pagination={{ pageSize: 12 }} />
      </Card>

      <Modal
        title={editingLeave ? "Update Leave History" : "New Leave Entry"}
        open={modalVisible}
        onOk={() => form.submit()}
        onCancel={() => setModalVisible(false)}
        confirmLoading={loading}
        width={750}
        destroyOnClose
        bodyStyle={{ paddingTop: 20 }}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="employee_id" label={<b>Target Employee</b>} rules={[{ required: true }]}>
                <Select showSearch optionFilterProp="children" disabled={!!editingLeave || !!selectedEmployee}>
                  {employees.map(emp => (
                    <Option key={emp.id} value={emp.id}>{emp.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="leave_type" label={<b>Type of Relief</b>} rules={[{ required: true }]}>
                <Select>
                  <Option value="Annual Leave">Annual Leave</Option>
                  <Option value="Sick Leave">Sick Leave</Option>
                  <Option value="Emergency Leave">Emergency Leave</Option>
                  <Option value="Unpaid Leave">Unpaid Leave</Option>
                  <Option value="Other">Other</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="dates"
                label={<b>From - To Dates</b>}
                rules={[
                  { required: true, message: 'Please select leave start and end dates' },
                  {
                    validator: (_, values) => {
                      if (values && values.length === 2 && values[0] && values[1]) {
                        if (values[1].isBefore(values[0], 'day')) {
                          return Promise.reject(new Error('End date must be on or after start date'));
                        }
                      }
                      return Promise.resolve();
                    }
                  }
                ]}
              >
                <RangePicker
                  style={{ width: '100%', borderRadius: 8 }}
                  format="DD/MM/YYYY"
                  inputReadOnly={true}
                  placeholder={['Start', 'End']}
                  getPopupContainer={trigger => trigger.parentNode}
                  onOpenChange={blockPickerScroll}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="actual_return_date"
                label={<b>Actual Return Date</b>}
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (value) {
                        const dates = getFieldValue('dates');
                        if (dates && dates[0] && moment(value).isBefore(moment(dates[0]).startOf('day'))) {
                          return Promise.reject(new Error('Actual return date cannot be before leave start date'));
                        }
                      }
                      return Promise.resolve();
                    }
                  })
                ]}
              >
                <DatePicker
                  style={{ width: '100%', borderRadius: 8 }}
                  format="DD/MM/YYYY"
                  inputReadOnly={true}
                  allowClear={true}
                  placeholder="Select return date"
                  disabledDate={(current) => {
                    const dates = form.getFieldValue('dates');
                    if (dates && dates[0]) {
                      return current && current.isBefore(dates[0].startOf('day'));
                    }
                    return false;
                  }}
                  getPopupContainer={trigger => trigger.parentNode}
                  onOpenChange={blockPickerScroll}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="reason" label={<b>Internal Remarks</b>}>
            <TextArea rows={4} placeholder="Specific notes about this leave..." />
          </Form.Item>

          <Form.Item label={<b>Attached Application / Document</b>}>
            <Upload beforeUpload={() => false} fileList={fileList} onChange={({ fileList }) => setFileList(fileList.slice(-1))} maxCount={1}>
              <Button icon={<UploadOutlined />}>Select File</Button>
            </Upload>
            {editingLeave?.leave_application_form && (
              <div style={{ marginTop: 8 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>Current: </Text>
                <a href={`${imagepath}${editingLeave.leave_application_form}`} target="_blank" rel="noreferrer" style={{ fontSize: 12 }}>
                  {editingLeave.original_filename || 'File Link'}
                </a>
              </div>
            )}
          </Form.Item>
        </Form>
      </Modal>

      <style dangerouslySetInnerHTML={{
        __html: `
        .ant-table-thead > tr > th {
          background: #fafafa !important;
          font-weight: 600 !important;
          border-bottom: 2px solid #f0f0f0 !important;
        }
        .ant-card {
           box-shadow: 0 4px 12px rgba(0,0,0,0.03);
        }
      `}} />
    </div>
  );
};

export default LeaveHistory;
